import { FC } from 'react';
import 'primeicons/primeicons.css';

import { useAuthStore, UserRoles } from '@/stores/useAuthStore.ts';

type VoucherTableHeaderProps = {
  searchText: string;
  setSearchText: (text: string) => void;
  onGeneratePdf: (() => void) | null;
  selectedCount: number;
  batchCount: number;
  onAddComplimentaryVoucher?: () => void;
  isGeneratingPdf: boolean;
};

const SearchBar: FC<{
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
}> = ({
  value,
  onChange,
  placeholder = 'Search vouchers or event names...',
}) => (
  <div className="relative w-full max-w-none md:max-w-sm flex-1 md:flex-none">
    <i className="pi pi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg shadow-sm
                 focus:ring-1 focus:ring-primary-100 focus:border-primary-100
                 focus:outline-none transition text-gray-700"
    />
    {value ? (
      <button
        type="button"
        aria-label="Clear search"
        onClick={() => onChange('')}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
      >
        <i className="pi pi-times text-base" />
      </button>
    ) : null}
  </div>
);

const VoucherTableHeader: FC<VoucherTableHeaderProps> = ({
  searchText,
  setSearchText,
  onGeneratePdf,
  selectedCount,
  batchCount,
  onAddComplimentaryVoucher,
  isGeneratingPdf,
}) => {
  const { role } = useAuthStore();
  const isAdmin = role === UserRoles.ADMIN;

  if (isAdmin) {
    return (
      <div className="flex flex-col gap-4 mb-4 ml-3 py-2">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
          <div className="flex items-center gap-4 text-primary-300">
            <i className="pi pi-ticket text-3xl" aria-hidden="true" />
            <h2 className="text-2xl font-semibold">
              Vouchers <span className="text-lg">({batchCount})</span>
            </h2>
          </div>

          <div className="flex flex-col md:flex-row md:items-center items-stretch gap-3 w-full md:w-auto mt-4 md:mt-0">
            {Boolean(onGeneratePdf) && (
              <button
                disabled={isGeneratingPdf}
                onClick={onGeneratePdf}
                className="py-2 px-8 rounded-lg transition-all whitespace-nowrap border font-medium w-full md:w-auto
                bg-white border-honoursOrange text-honoursOrange hover:opacity-80
                disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50"
              >
                {isGeneratingPdf ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="pi pi-spin pi-spinner" />
                    Generating...
                  </span>
                ) : (
                  `Generate PDF (${selectedCount})`
                )}
              </button>
            )}

            {onAddComplimentaryVoucher ? (
              <button
                onClick={onAddComplimentaryVoucher}
                className="bg-honoursOrange hover:bg-honoursOrange/90 text-white text-sm px-8 py-2 rounded-lg transition-colors whitespace-nowrap border border-honoursOrange w-full md:w-auto font-medium"
              >
                + Complimentary Voucher
              </button>
            ) : null}
          </div>
        </div>

        <div className="flex w-full md:justify-end">
          <SearchBar value={searchText} onChange={setSearchText} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-4 ml-3 py-2">
      <div className="flex items-center gap-4 text-primary-300">
        <i className="pi pi-ticket text-3xl" aria-hidden="true" />
        <h2 className="text-2xl font-semibold">
          Vouchers <span className="text-lg">({batchCount})</span>
        </h2>
      </div>

      <div className="flex flex-col md:flex-row w-full md:w-auto flex-1 justify-start md:justify-end items-stretch md:items-center gap-3 mt-4 md:mt-0">
        <SearchBar value={searchText} onChange={setSearchText} />

        {Boolean(onGeneratePdf) && (
          <button
            disabled={isGeneratingPdf}
            onClick={onGeneratePdf}
            className="bg-honoursOrange border-honoursOrange text-white py-2 px-8 rounded-lg transition-all whitespace-nowrap border font-medium w-full md:w-auto
            hover:bg-honoursOrange/90 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-honoursOrange"
          >
            {isGeneratingPdf ? (
              <span className="flex items-center justify-center gap-2">
                <i className="pi pi-spin pi-spinner" />
                Generating...
              </span>
            ) : (
              `Generate PDF (${selectedCount})`
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default VoucherTableHeader;
